import { QueryFactory } from '@lmt/shared-types';
import { sendRefreshTokenRequest } from '@lmt/unicorn-shared/app/api/auth/auth-client';
import { APP_VERSION } from '@lmt/unicorn-shared/app/constants/constants';

import { miniProfilerInterceptor } from '@lmt/unicorn-shared/app/helpers/MiniProfiler';
import { sessionAxiosInterceptor } from '@lmt/unicorn-shared/app/helpers/session-helper';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import i18n from 'i18next';
import { FeatureFlags } from '../constants/featureFlags';
import { GlobalState, UniDispatch } from '../store/types';
import { getAuthData, logOut, setAuthData } from '../features/auth/operations';

function createInjectTimezoneInterceptor() {
  const timeZoneOffset = new Date().getTimezoneOffset();
  return async (config: InternalAxiosRequestConfig) => {
    config.headers = config.headers ?? {};
    config.headers.Timezone = timeZoneOffset.toString();
    return config;
  };
}

const requestLoggingInterceptor = (config: InternalAxiosRequestConfig) => {
  console.log('http.request', config.method, config.url);
  return config;
};
export const responseErrorLoggingInterceptor = async (error: {
  response: AxiosResponse<any>;
  request: InternalAxiosRequestConfig;
  toJSON(): string;
}) => {
  console.error(
    'http.request.error',
    JSON.stringify(error?.toJSON?.()),
    error.response?.data,
  );

  throw error;
};

async function injectLanguageInterceptor(config: InternalAxiosRequestConfig) {
  const language = i18n.language;
  config.headers = config.headers ?? {};
  config.headers['Accept-Language'] = language;
  return config;
}

export function initializeAxios(
  getState: () => GlobalState,
  dispatch: UniDispatch,
) {
  // will be needed for nginx if we'd use it
  // axios.defaults.headers.common = {
  //   'X-FORWARDED-HOST': X_FORWARDED_HOST,
  // };

  createAuthRefreshInterceptor(
    axios,
    async (failedRequest: any): Promise<void> => {
      const refreshToken = getAuthData()!.refreshToken;
      if (refreshToken) {
        try {
          const result = await sendRefreshTokenRequest(refreshToken);

          setAuthData(result);
          failedRequest.response.config.headers['Authorization'] =
            'Bearer ' + result.access_token;
        } catch (e: any) {
          if (e.message === 'Login_Unknown_Failure') {
            logOut();
            return;
          }
          throw e;
        }
      }
    },
    {
      skipWhileRefreshing: false,
    },
  );
  axios.interceptors.request.use(
    axiosAuthInterceptor(() => getAuthData()?.accessToken),
  );
  axios.interceptors.request.use(createInjectTimezoneInterceptor());
  axios.interceptors.request.use(injectLanguageInterceptor);
  axios.interceptors.request.use(sessionAxiosInterceptor);
  axios.interceptors.request.use(requestLoggingInterceptor);
  axios.interceptors.response.use(null, responseErrorLoggingInterceptor);
  axios.interceptors.response.use(null, blobResponseErrorInterceptor);

  if (FeatureFlags.EnableMiniProfiler) {
    axios.interceptors.response.use(miniProfilerInterceptor, undefined);
  }
  return axios;
}

/**
 * This interceptor is used to intercept all http calls via axios,
 * and place token within authorization header.
 */
function axiosAuthInterceptor(tokenSource: () => string | undefined) {
  return async (config: InternalAxiosRequestConfig) => {
    const token = tokenSource();
    if (
      token &&
      !config.url?.endsWith('/connect/token') &&
      !config.url?.endsWith('/connect/revocation')
    ) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };
}

/**
 * Intercepts error responses for blob requests (e.g. file downloading) and parses the response
 * since it's probably JSON which isn't handled if `request.responseType === 'blob'`
 * See for details: https://stackoverflow.com/questions/56286368/how-can-i-read-http-errors-when-responsetype-is-blob-in-axios-with-vuejs
 * Fixes: #48233: No error message when document is not found
 */
export const blobResponseErrorInterceptor = async (error: {
  response: AxiosResponse<any>;
  request: InternalAxiosRequestConfig;
}) => {
  const response = error.response;
  const request = error.request;
  const data = response?.data;

  if (request?.responseType === 'blob' && data instanceof Blob && data.type) {
    const text = await data.text();

    response.data = data.type.toLowerCase().includes('json')
      ? JSON.parse(text)
      : text;
  }

  throw error;
};

export class ClientFactory {
  public static get DoctorClient() {
    return QueryFactory.DoctorClient;
  }

  public static get PatientClient() {
    return QueryFactory.PatientClient;
  }

  public static get RoleClient() {
    return QueryFactory.RoleClient;
  }

  public static get TenantClient() {
    return QueryFactory.TenantClient;
  }

  public static get TagClient() {
    return QueryFactory.TagClient;
  }

  public static get VisitTypeClient() {
    return QueryFactory.VisitTypeClient;
  }

  public static get DataBlockTypeClient() {
    return QueryFactory.DataBlockTypeClient;
  }

  public static get DataBlockFieldClient() {
    return QueryFactory.DataBlockFieldClient;
  }

  public static get MeasurementMappingClient() {
    return QueryFactory.MeasurementMappingClient;
  }

  public static get DataBlockClient() {
    return QueryFactory.DataBlockClient;
  }

  public static get AppointmentsClient() {
    return QueryFactory.AppointmentEventsClient;
  }

  public static get ResourceClient() {
    return QueryFactory.ResourceClient;
  }

  public static get CaseClient() {
    return QueryFactory.CaseClient;
  }

  public static get FileClient() {
    return QueryFactory.FileClient;
  }

  public static get MergeFieldClient() {
    return QueryFactory.MergeFieldClient;
  }

  public static get DocumentTemplateClient() {
    return QueryFactory.DocumentTemplateClient;
  }

  public static get DocumentClient() {
    return QueryFactory.DocumentClient;
  }

  public static get DocumentTemplateRuleClient() {
    return QueryFactory.DocumentTemplateRuleClient;
  }

  public static get HL7FilterClient() {
    return QueryFactory.HL7FilterClient;
  }

  public static get HL7MappingClient() {
    return QueryFactory.HL7MappingClient;
  }

  public static get HL7MessageTypeMappingClient() {
    return QueryFactory.HL7MessageTypeMappingClient;
  }

  public static get GDTOutgoingTemplateClient() {
    return QueryFactory.GDTOutgoingTemplatesClient;
  }

  public static get HL7OutgoingTemplateClient() {
    return QueryFactory.HL7OutgoingTemplatesClient;
  }

  public static get GDTMessageClient() {
    return QueryFactory.GDTMessageClient;
  }

  public static get HL7MessageClient() {
    return QueryFactory.HL7MessageClient;
  }

  public static get GDTOutgoingMessageClient() {
    return QueryFactory.GDTOutgoingMessageClient;
  }

  public static get HL7OutgoingMessageClient() {
    return QueryFactory.HL7OutgoingMessageClient;
  }

  public static get CalendarConfigurationClient() {
    return QueryFactory.CalendarConfigurationClient;
  }

  public static get UIConfigurationClient() {
    return QueryFactory.UIConfigurationClient;
  }

  public static get PatientCommentClient() {
    return QueryFactory.PatientCommentClient;
  }

  public static get PatientListConfigurationClient() {
    return QueryFactory.PatientListConfigurationClient;
  }

  public static get GDTMappingClient() {
    return QueryFactory.GDTMappingClient;
  }

  public static get VisitCardSettingsClient() {
    return QueryFactory.VisitCardSettingsClient;
  }

  public static get NoteClient() {
    return QueryFactory.NoteClient;
  }
}
